/* eslint-disable react-hooks/rules-of-hooks */
import _ from 'lodash'
import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import type { Component, RefSchemas } from '@wix/thunderbolt-becky-types'
import type { Page, Environment, Node, Observers, DataNode, RunAndReportFn } from './types'
import { DepsTracker } from './depsTracker'
import { findDataItemRefs, defaultDataNode } from './dataResolver'
import { createDataKey, createEnvKey, createNodeKey } from './createNodeKeys'
import { getComponentsWithRepeatedItems } from './repeaters'
import { fixStructure } from '@wix/thunderbolt-catharsis'
import type { Ref } from '@wix/thunderbolt-types'
import { buildEnvironment } from './environment'

const stripHash = (str: string) => (str.startsWith('#') ? str.slice(1) : str)
const createDataRef = (path: Array<string>) => ({ $type: 'ref', refPath: ['data', ...path] } as Ref<unknown>)

export class ViewerStateManager {
	// models
	#page: Page
	#env: Environment
	// Configurations
	#isEditor: boolean
	// nodes result memoization
	#memo = new Map<string, any>()
	// deps tracking - used only for editor
	#depTracker = new DepsTracker()
	// nodes
	#dataNodes: Record<string, DataNode<any>>
	#nodes: Record<string, Node>
	#renderableNodesMap: Record<string, Array<string>>
	#runAndReport: RunAndReportFn
	#dynamicSchemas: RefSchemas

	constructor(
		beckyModel: BeckyModel,
		dataNodes: Record<string, DataNode<any>>,
		nodes: Array<Node>,
		renderableNodeToFeatures: Record<string, Array<string>>,
		runAndReport: RunAndReportFn = (fn: any) => fn()
	) {
		this.#runAndReport = runAndReport
		// Extract and merge dynamic schemas from beckyModel
		this.#dynamicSchemas = {}

		const addDynamicSchemas = (addedSchemas: RefSchemas) => {
			for (const mapName in addedSchemas) {
				if (!this.#dynamicSchemas[mapName]) {
					this.#dynamicSchemas[mapName] = {}
				}
				Object.assign(this.#dynamicSchemas[mapName], addedSchemas[mapName])
			}
		}

		// if experiment is on, use dynamicSchemas instead of appDefIdToRefs
		if (beckyModel.experiments['specs.thunderbolt.reuseDynamicRefSchemas']) {
			addDynamicSchemas(beckyModel.dynamicSchemas)
		} else {
			Object.values(beckyModel.appDefIdToRefs).forEach((appSchemas) => {
				addDynamicSchemas(appSchemas)
			})
		}

		const { structure, data, pageCompId } = beckyModel
		const components = getComponentsWithRepeatedItems({
			structure,
			data,
			pageCompId,
		})
		this.#page = {
			structure: components.map(fixStructure).reduce<BeckyModel['structure']>((acc, comp) => {
				acc[comp.id] = comp
				return acc
			}, {}),
			data,
		}

		this.#env = buildEnvironment(beckyModel)
		this.#isEditor = beckyModel.editorName !== 'Unknown'

		// Load nodes
		this.#dataNodes = dataNodes
		this.#nodes = nodes.reduce((acc, node) => {
			acc[node.name] = node
			return acc
		}, {} as Record<string, Node>)
		this.#renderableNodesMap = renderableNodeToFeatures
	}

	#createObservers(nodeKey: string): Observers {
		const useEnv: Observers['useEnv'] = (envRef) => {
			const [namespace, ...path] = envRef.refPath
			if (namespace !== 'environment') {
				throw new Error("invalid envRef: should start with 'environment'")
			}
			const fixedPath = path[0] === 'atoms' ? path.slice(1) : path
			if (this.#isEditor) {
				// ToDo: Make a blocklist of envs that cannot change to save space
				this.#depTracker.add(createEnvKey(fixedPath), nodeKey)
			}
			return _.get(this.#env, fixedPath) || envRef.defaultValue
		}
		const useData: Observers['useData'] = (dataRef) => {
			const [namespace, ...path] = dataRef.refPath
			if (namespace !== 'data') {
				throw new Error("invalid dataRef: should start with 'data'")
			}
			if (path.length > 2) {
				const baseNodeValue = useData({ ...dataRef, refPath: dataRef.refPath.slice(0, 3) })
				return _.get(baseNodeValue, path.slice(2))
			}
			if (this.#isEditor) {
				this.#depTracker.add(createDataKey(path), nodeKey)
			}

			return this.#executeDataNode(path)
		}
		const useNode: Observers['useNode'] = (depNode, depCompId) => {
			const depNodeId = typeof depNode === 'string' ? depNode : depNode.name
			if (this.#isEditor) {
				this.#depTracker.add(createNodeKey(depCompId, depNodeId), nodeKey)
			}
			return this.#executeNode(this.#page.structure[depCompId], depNodeId)
		}
		return { useEnv, useData, useNode }
	}

	// TODO: Move this logic to a util (will have to get useData though..)
	#executeDataNode(path: Array<string>): any {
		// console.log(`executing data node "${path}"`)
		const [dataMap, dataItemId] = path
		if (!dataItemId) {
			// For example, layoutQuery for a component without layoutQuery
			return undefined
		}
		if (dataMap === 'components') {
			// for example, VariantRelation -> from
			// ToDo: Should we resolve these from structure.json?
			return this.#page.structure[dataItemId]
		}
		const dataNodeKey = createDataKey(path)
		if (this.#memo.get(dataNodeKey)) {
			return this.#memo.get(dataNodeKey)
		}
		// dataItem does not exist / not a dataItem (for example, ['data', 'theme_data', 'THEME_DATA', 'fontMap'])
		const dataItem = _.get(this.#page.data, path)
		if (!dataItem || !dataItem.type) {
			return dataItem
		}
		const dataNode = this.#dataNodes[dataItem.type] || defaultDataNode
		const observers = this.#createObservers(dataNodeKey)
		const resolvedRefs = {}
		const dataItemRefs = findDataItemRefs(dataItem, path, this.#dynamicSchemas)
		dataItemRefs.refs.forEach(({ innerPath, referencedMap }) => {
			const refIdOrArray = _.get(dataItem, innerPath)
			_.set(
				resolvedRefs,
				innerPath,
				Array.isArray(refIdOrArray)
					? refIdOrArray.map((refId) => observers.useData(createDataRef([referencedMap, stripHash(refId)])))
					: observers.useData(createDataRef([referencedMap, stripHash(refIdOrArray)]))
			)
		})
		const result = dataItemRefs.alreadyResolved ? dataItem : dataNode.execute(dataItem, resolvedRefs, observers)
		this.#memo.set(dataNodeKey, result)
		return result
	}

	#executeNode(component: Component, nodeId: string) {
		if (!component) {
			return null
		}
		const compId = component.id
		const nodeKey = createNodeKey(compId, nodeId)
		// console.log(`executing node "${nodeId}" for compId="${compId}"`)
		if (this.#memo.get(nodeKey)) {
			return this.#memo.get(nodeKey)
		}
		// Execute the node and memoize its result
		const node = this.#nodes[nodeId]
		const observers = this.#createObservers(nodeKey) as Observers
		const result = node.execute(component, observers)
		this.#memo.set(nodeKey, result)
		return result
	}

	getCurrentStructure() {
		return this.#page.structure
	}

	compute() {
		const result = {}
		for (const [nodeId, featureNames] of Object.entries(this.#renderableNodesMap)) {
			this.#runAndReport(() => {
				for (const component of Object.values(this.#page.structure)) {
					const nodeResult = this.#executeNode(component, nodeId)
					featureNames.forEach((featureName) =>
						_.set(result, [component.id, featureName, nodeId], nodeResult)
					)
				}
			}, `executeFeature-${nodeId}`)
		}
		return result
	}

	getDependencies() {
		return this.#depTracker.getDependencies()
	}
}
